import { FC, FormEvent, useMemo, useState } from 'react';

import { AdminCategory, AdminItem, Tag } from '../../../api/client';
import { formatMoney, toCents } from '../../../shared/functions/formatter';
import { Button, CardTitle, CurrencyField, DangerZone, Dialog, Field, FormGrid, Input, Meta, Stack, Switch } from '../../../ui';
import BundleEligibleItemPicker from './BundleEligibleItemPicker';
import BundlePricingSummary from './BundlePricingSummary';
import BundleRequirementGroup from './BundleRequirementGroup';
import EligibleItemsPreview from './EligibleItemsPreview';
import { BundleFormState, GroupDraft, GroupStats, SetBundleField, emptyGroup, isSameBundleForm, plural, rangeFor } from './types';

type BundleOfferEditorProps = {
  initial: BundleFormState;
  categories: AdminCategory[];
  items: AdminItem[];
  tags: Tag[];
  submitLabel: string;
  busy: boolean;
  onSubmit: (form: BundleFormState) => void;
  /** Editing an existing bundle: offers Cancel, tracks changes, allows delete. */
  onCancel?: () => void;
  onDelete?: () => void;
};

/**
 * One editable bundle.
 *
 * The same editor creates and edits, so the validation, the requirement groups
 * and the saving calculation cannot drift between the two paths. It is rendered
 * inside the New bundle card, and inline when an existing bundle is expanded.
 */
const BundleOfferEditor: FC<BundleOfferEditorProps> = ({
  initial,
  categories,
  items,
  tags,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
  onDelete,
}) => {
  const [form, setForm] = useState<BundleFormState>(initial);
  // The baseline the draft is compared against. The editor remounts on a new
  // bundle or after a create, so `initial` is stable for its lifetime.
  const [savedForm] = useState<BundleFormState | null>(initial);
  const [nameTouched, setNameTouched] = useState(false);
  const [priceTouched, setPriceTouched] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  /** The group whose selected-items picker is open, if any. */
  const [pickerKey, setPickerKey] = useState<string | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelection, setPickerSelection] = useState<number[]>([]);
  const [viewAll, setViewAll] = useState<{ title: string; items: AdminItem[] } | null>(null);

  const categoryById = useMemo(() => new Map(categories.map((category) => [category.id, category] as const)), [categories]);
  const itemById = useMemo(() => new Map(items.map((item) => [item.id, item] as const)), [items]);
  const tagById = useMemo(() => new Map(tags.map((tag) => [tag.id, tag] as const)), [tags]);

  const setField: SetBundleField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const updateGroup = (key: string, patch: Partial<GroupDraft>) => {
    setForm((current) => ({
      ...current,
      groups: current.groups.map((group) => (group.key === key ? { ...group, ...patch } : group)),
    }));
  };

  const removeGroup = (key: string) => {
    setForm((current) => ({
      ...current,
      groups: current.groups.length <= 1 ? current.groups : current.groups.filter((group) => group.key !== key),
    }));
  };

  const openPicker = (group: GroupDraft) => {
    setPickerSelection([...group.itemIds]);
    setPickerSearch('');
    setPickerKey(group.key);
  };

  const confirmPicker = () => {
    if (pickerKey !== null) updateGroup(pickerKey, { itemIds: [...pickerSelection] });
    setPickerKey(null);
  };

  const stats = form.groups.map((group) => rangeFor(group, items, itemById));
  const componentsMin = stats.reduce((sum, stat) => sum + stat.min, 0);
  const componentsMax = stats.reduce((sum, stat) => sum + stat.max, 0);

  /** Whether a group names a target that currently has at least one item. */
  const statComplete = (stat: GroupStats): boolean => {
    const group = stat.group;
    if (group.quantity < 1) return false;
    if (group.source === 'category') return group.categoryId !== 0 && stat.count > 0;
    if (group.source === 'tag') return group.tagId !== 0 && stat.count > 0;
    return group.itemIds.length > 0;
  };

  const groupProblem = (stat: GroupStats): string | null => {
    const group = stat.group;
    if (group.source === 'category' && group.categoryId === 0) return 'Choose a category for this group.';
    if (group.source === 'tag' && group.tagId === 0) return 'Choose a tag for this group.';
    if (group.source === 'items' && group.itemIds.length === 0) return 'Select at least one item for this group.';
    if (statComplete(stat) && stat.count === 0) return 'Nothing is eligible for this group right now.';
    return null;
  };

  const priceCents = toCents(form.price);
  const priceParsed = Number.isFinite(priceCents);
  const effectivePrice = priceParsed ? priceCents : 0;
  const priceEntered = form.price.trim().length > 0;
  const groupsComplete = form.groups.length > 0 && stats.every(statComplete);
  // Only comparable once every group is complete: before that every price would
  // look too high, and the warning would be noise rather than help.
  const priceTooHigh = groupsComplete && priceParsed && priceCents > componentsMax;
  const priceValid = priceEntered && priceParsed && priceCents >= 0 && !priceTooHigh;
  const nameValid = form.name.trim().length > 0;
  const valid = nameValid && groupsComplete && priceValid;

  const priceError = !priceEntered
    ? 'Bundle price is required.'
    : !priceParsed
      ? 'Enter a valid price.'
      : priceCents < 0
        ? 'Price cannot be negative.'
        : 'Price is above the dearest eligible items.';
  const showNameError = nameTouched && !nameValid;
  const showPriceError = priceTouched && !priceValid;

  const savingMin = componentsMin - effectivePrice;
  const savingMax = componentsMax - effectivePrice;
  // The warning the spec asks for: the cheapest legal pick does not save.
  const savingWarning = groupsComplete && priceParsed && !priceTooHigh && savingMin <= 0;

  const dirty = savedForm !== null && !isSameBundleForm(form, savedForm);

  const sourceName = (group: GroupDraft): string => {
    if (group.source === 'category') return categoryById.get(group.categoryId)?.name ?? 'a category';
    if (group.source === 'tag') return tagById.get(group.tagId)?.name ?? 'a tag';
    const titles = group.itemIds.map((id) => itemById.get(id)?.title).filter((title): title is string => Boolean(title));
    if (titles.length === 0) return 'the selected items';
    return titles.length === 1 ? titles[0] : plural(titles.length, 'selected item');
  };

  const howItWorks =
    form.groups.length === 0
      ? 'Add a group to describe what customers choose.'
      : 'Customers choose ' +
        form.groups.map((group) => group.quantity + ' from ' + sourceName(group)).join(' and ') +
        ' for ' +
        formatMoney(effectivePrice) +
        (priceValid && savingMax > 0 ? ', saving up to ' + formatMoney(savingMax) + '.' : '.');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!valid || busy || (onCancel !== undefined && !dirty)) return;
    onSubmit(form);
  };

  return (
    <form className="discountEditor" onSubmit={handleSubmit}>
      <section className="discountEditorSection">
        <CardTitle level={3}>Bundle details</CardTitle>
        <FormGrid columns={2}>
          <Field className="discountFieldGrow" label="Name" error={showNameError ? 'Name is required.' : undefined}>
            <Input
              value={form.name}
              placeholder="Art Print x2"
              maxLength={160}
              onChange={(event) => {
                setField('name', event.target.value);
                setNameTouched(true);
              }}
            />
          </Field>

          <CurrencyField
            className="discountFieldPrice"
            inputClassName="discountPriceField"
            label="Bundle price"
            value={form.price}
            placeholder="0.00"
            autoComplete="off"
            error={showPriceError ? priceError : undefined}
            onChange={(event) => {
              setField('price', event.target.value);
              setPriceTouched(true);
            }}
          />

          <Switch className="discountActive" checked={form.active} label="Active" onChange={(checked) => setField('active', checked)} />
        </FormGrid>
      </section>

      <section className="discountEditorSection">
        <CardTitle level={3}>What can customers choose?</CardTitle>
        <Meta>Each group is a "choose N from ..." rule. A bundle needs every group filled.</Meta>
        <div className="discountGroups">
          {form.groups.map((group, index) => (
            <BundleRequirementGroup
              key={group.key}
              group={group}
              index={index}
              totalGroups={form.groups.length}
              stat={stats[index]}
              problem={groupProblem(stats[index])}
              categories={categories}
              tags={tags}
              itemById={itemById}
              onChange={updateGroup}
              onRemove={removeGroup}
              onOpenPicker={openPicker}
              onViewAll={(title, eligible) => setViewAll({ title, items: eligible })}
            />
          ))}
        </div>
        <Button
          variant="secondary"
          className="discountAddGroup"
          onClick={() => setForm((current) => ({ ...current, groups: [...current.groups, emptyGroup(categories)] }))}
        >
          + Add another group
        </Button>
      </section>

      <BundlePricingSummary
        componentsMin={componentsMin}
        componentsMax={componentsMax}
        effectivePrice={effectivePrice}
        savingMin={savingMin}
        savingMax={savingMax}
        groupsComplete={groupsComplete}
        savingWarning={savingWarning}
      />

      <section className="discountEditorSection">
        <CardTitle level={3}>How it works</CardTitle>
        <p className="discountHowItWorks">{howItWorks}</p>
      </section>

      <Stack direction="row" gap={5}>
        <Button variant="primary" type="submit" disabled={!valid || busy || (onCancel !== undefined && !dirty)}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
        {onCancel && (
          <Button variant="secondary" type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
      </Stack>

      {onDelete && (
        <DangerZone
          className="discountDanger"
          title="Delete bundle"
          description="It disappears from the till. Sales already recorded are not affected."
          action={
            <Button variant="destructive" onClick={() => setConfirmDelete(true)} disabled={busy}>
              Delete bundle
            </Button>
          }
        />
      )}

      <BundleEligibleItemPicker
        open={pickerKey !== null}
        items={items}
        selectedIds={pickerSelection}
        search={pickerSearch}
        onSearchChange={setPickerSearch}
        onToggle={(itemId) =>
          setPickerSelection((current) => (current.includes(itemId) ? current.filter((entry) => entry !== itemId) : [...current, itemId]))
        }
        onCancel={() => setPickerKey(null)}
        onConfirm={confirmPicker}
      />

      <EligibleItemsPreview
        open={viewAll !== null}
        title={viewAll?.title ?? 'Items'}
        items={viewAll?.items ?? []}
        onClose={() => setViewAll(null)}
      />

      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={'Delete ' + (form.name.trim() || 'this bundle') + '?'}
        description="The bundle is removed from the till and from this list. Sales already recorded keep their items and are not affected."
        tone="danger"
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onDelete} disabled={busy}>
              Delete bundle
            </Button>
          </>
        }
      />
    </form>
  );
};

export default BundleOfferEditor;
